import styled from "styled-components";

import { lgToneInk } from "manager/components/app-summary/AppListV2/StatusIcon";
import { COLORS } from "shared/config/constants";

import { VERO1_ACCENT } from "./styled";

/** While its editor is open the row carries a faint tint, bled across the
 *  panel gutters so the whole band reads as the thing being edited (spec §09). */
export const RowContainer = styled.div<{ $editing?: boolean; $record?: boolean }>`
  position: relative;
  padding: 12px 0 14px 16px;
  border-bottom: 1px solid ${COLORS.borders};

  /* a submitted/set-aside review is a record — rows read slightly muted so
     the panel never looks like an editable draft (2026-08-28) */
  ${({ $record }) => $record && "opacity: 0.85;"}

  ${({ $editing }) =>
    $editing &&
    `background: #fbfafe;
     margin: 0 -18px;
     padding-left: 34px;
     padding-right: 18px;

     /* the card spans the full row — back across the pip column to the
        18px gutter, its closing gap carried by the row's own padding */
     > [data-testid="vero1-row-editor"] {
       margin: 8px 0 0 -16px;
     }`}
`;

/** Violet pip flags rows the agent modified — not unread rows (spec §3.1). */
/** The "a human touched this row" pip — an in-flow flex child at the head of
 *  the marker row, immediately before the result marker, exactly as the spec
 *  draws it (secondary-600, 7px). A fixed-width slot holds the column so an
 *  untouched row's marker aligns with a touched one's. */
export const PipSlot = styled.span`
  flex: 0 0 auto;
  width: 7px;
  height: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

export const ModifiedPip = styled.span`
  display: inline-block;
  flex: 0 0 auto;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${VERO1_ACCENT};
`;

/** Never wraps: the icon, title, diff pill, and edit affordance hold one
 *  line — a long title wraps its own text inside the flexible middle. */
/**
 * Every row centres its marker, title, value and edit control on one line.
 *
 * The canvas draws numeric rows on the text baseline, but its numeric rows
 * carry a bare spacer where ours renders a real marker — baseline drops a
 * bordered circle off the line. Centring reads better with the marker present
 * and keeps one rule for both row types.
 */
export const RowHeader = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 8px;
  min-width: 0;
`;

export const RowTitle = styled.div<{ $confirming?: boolean }>`
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  justify-content: space-between;
  column-gap: 10px;

  p {
    margin: 0;
  }

  /* while a remove-confirm sits beside it, hold the label to one clipped
     line so the confirm never squeezes the title into a ragged wrap */
  ${({ $confirming }) =>
    $confirming
      ? `
    flex-wrap: nowrap;
    span {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  `
      : ""}
`;

/** Every drawn row title is 500/13px — boolean checks and numeric facts
 *  alike (spec §08: all eight rows). The figure beside a numeric title is
 *  what carries the heavier 500/14px tabular treatment. */
export const RowLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${COLORS.primary};
`;

/* Drawn value type (spec 4.1 / 8.2): 500 13px ui-monospace — ratios and
   money figures read as data, not prose, on both the panel and the tab. */
export const RowValue = styled.span`
  flex: 0 0 auto;
  font:
    500 13px ui-monospace,
    Menlo,
    Consolas,
    monospace;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: ${COLORS.primary};
`;

/** Applicant attribution on its own line under the title — the header line
 *  stays icon + title + pill + edit. Indent aligns with the title. */
export const ApplicantLine = styled.div`
  margin: 2px 0 0 30px;
  font-size: 11px;
  color: ${COLORS.secondary};
  overflow-wrap: anywhere;
`;

export const RowBody = styled.div`
  padding-top: 2px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  /* Findings prose aligns under the title, not the result icon. The shared
     Typography renders a <span>, so this targets the body's direct text
     children rather than a <p> that never exists. */
  > span,
  > p,
  > ul,
  > div:not([data-testid="vero1-agent-block"]) {
    margin-left: 30px;
    font-size: 12px;
    line-height: 1.5;
  }
`;

/** Canvas 08 (360 redraw): the before→after pill left the row header and now
 *  opens the change strip inside the agent block, beside the reason chip —
 *  the header at 360 holds icon, title, value and the edit control only. */
export const ChangeStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

/** Diff pill, now on the change strip: struck before → after, "→ Unchanged" when only
 *  findings changed (mockup treatment). */
export const BeforeAfterPill = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  padding: 3px 7px;
  border-radius: 2px;
  background: #f0eef6;
  color: #4a4566;
  font-size: 10px;
  font-weight: 600;
`;

/** Canvas: the struck verdict keeps its color and weight — the line alone
 *  says it was replaced. */
export const ResultWord = styled.span<{
  $pass?: boolean;
  $struck?: boolean;
  $neutral?: boolean;
  $tone?: "pass" | "warn" | "fail" | null;
}>`
  color: ${({ $pass, $neutral, $tone }) =>
    $tone
      ? lgToneInk($tone)
      : $neutral
      ? COLORS.secondary
      : lgToneInk($pass ? "pass" : "fail")};
  ${({ $struck }) => $struck && "text-decoration: line-through;"}
`;

/**
 * Everything agent-authored sits in one indented block behind a violet rail —
 * Lumen's annotation above it stays plain (mockup treatment). The rail marks
 * authorship, not severity.
 */
export const AgentBlock = styled.div`
  margin-left: 30px;
  padding-left: 11px;
  border-left: 2px solid ${VERO1_ACCENT};
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

/** "Result changed by Chase Mariott · 2:41 PM" — the drawn line is 300/11px
 *  muted with the NAME alone lifted to 500 in text ink (spec §08), so the
 *  authorship reads without the whole line competing with the findings. */
export const AttributionLine = styled.span`
  font-size: 11px;
  font-weight: 300;
  color: ${COLORS.secondary};

  b {
    font-weight: 500;
    color: ${COLORS.primary};
  }
`;

/** Small eyebrow naming an agent-authored field (Findings / Leasing action). */
export const FieldLabel = styled.span`
  font-size: 8.5px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #595475;
  white-space: nowrap;

  svg {
    vertical-align: -1px;
  }
`;

/** Eyebrow beside its text, wrapping under it when the pane is narrow. */
export const LabeledLine = styled.div`
  /* canvas (360 redraw): the eyebrow STACKS above its text rather than
     sitting beside it — at 360 an inline label wrapped to two lines and
     pushed the findings into a narrow column */
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  > span:first-child {
    flex: 0 0 auto;
  }

  p {
    margin: 0;
    min-width: 0;
    overflow-wrap: anywhere;
  }
`;

/** The reason the agent picked, rose-tinted when the row fails (canvas §08).
 *  Reads in the client-facing wording, so it wears the panel's own type — the
 *  monospace here dated from when the chip showed the raw code. */
export const ReasonChip = styled.span<{ $fail?: boolean }>`
  align-self: flex-start;
  display: inline-block;
  /* the wording wraps where a code never did, so the box grows with it rather
     than holding a one-line height the second line spills out of */
  padding: 2px 10px;
  min-height: 20px;
  line-height: 1.35;
  border-radius: 2px;
  font-size: 11px;
  font-weight: 500;
  overflow-wrap: anywhere;
  color: ${({ $fail }) => ($fail ? COLORS.failedRed : COLORS.primary)};
  border: 1px solid ${({ $fail }) => ($fail ? "#e7b9c4" : "#b7b3c9")};
  background: ${({ $fail }) => ($fail ? "#fdf3f5" : "#f4f3f8")};
`;

/** One line per record on records criteria (evictions, criminal, collections). */
export const RecordList = styled.ul`
  /* body-aligned via RowBody's indent (no disc). Records read as evidence
     beneath Lumen's conclusion: a touch smaller and more muted than the
     reasoning above them (B2 soft hierarchy). No left rail — that is the
     agent-findings signature and must stay unique to it. */
  margin: 4px 0 0;
  padding: 0;
  list-style: none;

  li {
    margin-bottom: 3px;
  }

  li,
  li span {
    font-size: 11.5px;
    line-height: 1.5;
    color: ${COLORS.darkPurple};
  }
`;

/**
 * Rows Lumen returned data but no verdict for are awaiting the agent. The
 * marker is dashed and neutral — never red — so the row reads as an
 * invitation rather than a failure (punch list B2).
 */
export const UnassessedMarker = styled.span`
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  border: 1.5px dashed #ada8c2;
  border-radius: 50%;
`;

/** The pencil + trash sit in this tight right-anchored group, so the icons
 *  are a hair apart rather than each carrying its own 28px box of air. */
export const RowActions = styled.div`
  margin-left: auto;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 2px;
`;

export const EditAction = styled.button`
  border: 1px solid transparent;
  border-radius: 4px;
  background: none;
  width: 26px;
  height: 26px;
  justify-content: center;
  padding: 0;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  color: ${COLORS.secondary};
  cursor: pointer;

  &:hover {
    color: ${VERO1_ACCENT};
  }
`;

/** Inline "Remove? · Remove / Cancel" confirm that replaces the row's
 *  action icons while a delete is pending — destructive actions confirm,
 *  matching the panel's inline discard idiom rather than a modal. */
export const RemoveConfirm = styled.span`
  margin-left: auto;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
  font-size: 12px;
  color: ${COLORS.secondary};
`;

export const RemoveConfirmYes = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.failedRed};
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

export const RemoveConfirmNo = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-size: 12px;
  color: ${COLORS.secondary};
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

/** The trash affordance beside the pencil on an agent-added custom row —
 *  same 28px square as EditAction, but its hover is the delete-red so the
 *  destructive action never reads as just another edit. */
export const RemoveAction = styled.button`
  border: 1px solid transparent;
  border-radius: 4px;
  background: none;
  width: 26px;
  height: 26px;
  justify-content: center;
  padding: 0;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  color: ${COLORS.secondary};
  cursor: pointer;

  &:hover {
    color: ${COLORS.failedRed};
  }
`;

/** The pencil's replacement while its editor is open — a violet EDITING tag,
 *  turning rose NOT SAVED when the save was rejected (spec §09/§9.4). */
export const EditingTag = styled.span<{ $conflict?: boolean }>`
  flex: 0 0 auto;
  margin-left: auto;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ $conflict }) => ($conflict ? COLORS.failedRed : VERO1_ACCENT)};
`;

/** A quiet inline reveal ("+ Add …") — text-sized, unlike the square
 *  icon-button above, which clips any text label to its 28px box. */
export const EditorReveal = styled.button`
  border: none;
  background: none;
  padding: 0;
  align-self: flex-start;
  font-size: 13px;
  font-weight: 500;
  color: ${COLORS.secondary};
  cursor: pointer;

  &:hover {
    color: ${COLORS.primary};
  }
`;

/** Provisional treatment pending design: presentation only — storage stays
 *  binary; the chip follows the agent's chosen band reason code. */
/** Re-review triage: marks rows whose Lumen data moved since the prior
 *  version — the agent re-judges these; unchanged rows stand as last judged. */
export const AnnotationOnlyChip = styled.span`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #6f6a8a;
  border: 1px solid #d5d2e2;
  background: ${COLORS.white};
`;

/** Re-review badge on the header's right (spec 4.2): compact, amber, one line. */
export const ChangedChip = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  height: 18px;
  padding: 0 6px;
  border-radius: 2px;
  font-size: 10px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  color: #b35500;
  background: #ffe4cc;
`;

/** The amber marker on an incomplete row (spec §09) wears the canvas
 *  .chip.amber treatment — the same chip Conditional verdicts wear. */
export const IncompleteMarker = styled.span`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 9px;
  border-radius: 2px;
  font-weight: 500;
  line-height: 1;
  color: #b35500;
  background: #ffe4cc;
  margin: 4px 0;

  /* doubled: RowBody's "> span" text rule outweighs a single class */
  && {
    font-size: 11px;
  }
`;
